import React from 'react';
import PropTypes from 'prop-types';
import Option from '../Option';
import OtherOptionInput from './OtherOptionInput';
import OtherOptionControl from './OtherOptionControl';
import LoadMore from './LoadMore';

export class Options extends React.Component {
  // returns undefined if all the values are known options.
  // otherwise returns the first unknown value, assume that was entered in the "other" field.
  static getOtherInputValueAlreadySet(props) {
    const { value, options, otherEnabled } = props;

    if (otherEnabled) {
      const valuesToCheck = typeof value === 'string' ? [value] : value;
      return valuesToCheck.find(v => options.every(o => o !== v));
    }
    return undefined;
  }

  static _syncValuesAndOptions(value, options) {
    const copy = options.slice(0);
    if (value && options) {
      value.forEach(v => {
        const index = copy.indexOf(v);

        if (index !== -1) {
          copy.splice(index, 1);
        }

        copy.unshift(v);
      });
    }
    return copy;
  }

  static _getValue(value) {
    return typeof value === 'string' ? [value] : value;
  }

  constructor(props) {
    super(props);

    this.options = [];

    this._isExclusive = this._isExclusive.bind(this);
    this._handleOnClick = this._handleOnClick.bind(this);
    this._handleLoadMore = this._handleLoadMore.bind(this);
    this._getOptionComponents = this._getOptionComponents.bind(this);
    this._handleOnClickOther = this._handleOnClickOther.bind(this);
    this._handleOnChangeOtherInput = this._handleOnChangeOtherInput.bind(this);
    this.focus = this.focus.bind(this);

    const otherInputValueAlreadySet = Options.getOtherInputValueAlreadySet(props);

    this.state = {
      loadMax: false,
      otherChecked: otherInputValueAlreadySet ? true : false,
      otherInputValue: otherInputValueAlreadySet ? otherInputValueAlreadySet : ''
    };
  }

  focus() {
    if (this.options && this.options.length > 0) {
      this.options[0].focus();
    }
  }

  _getOptionComponents(options) {
    const { loadMax } = this.state;
    const { type, maxShown, initShown, loadMore, optionProps } = this.props;
    const value = Options._getValue(this.props.value);

    const optionsLength = options ? options.length : 0;
    const max = maxShown !== -1 && optionsLength > maxShown ? maxShown : optionsLength;
    const min = value && value.length > initShown ? value.length : initShown;
    const displayCount = !loadMax && loadMore ? min : max;

    if (options && options.length > 0) {
      const optionsShown = options.slice(0, displayCount);
      const components = optionsShown.map(option => (
        <Option
          key={option}
          id={option}
          text={option}
          checked={value.indexOf(option) !== -1}
          value={option}
          type={type}
          onChange={this._handleOnClick.bind(null, option)}
          {...optionProps}
          ref={option => {
            this.options.push(option);
          }}
        />
      ));

      return components;
    }
    return null;
  }

  // An exclusive option is one that is in a multi-value options list
  //   but isn't allowed to be selected along with other options.

  _isExclusive(option) {
    const { exclusiveOptions } = this.props;
    return exclusiveOptions && exclusiveOptions.indexOf(option) > -1;
  }

  _handleOnClick(option) {
    const { multiValue, id, onChange } = this.props;
    const value = Options._getValue(this.props.value);

    if (multiValue) {
      let newValue = value.slice(0, value.length);
      const index = newValue.indexOf(option);

      if (index !== -1) {
        // user is de-selecting the option.
        newValue.splice(index, 1);
      } else {
        // user is selecting the option.
        const oldValueIsExclusive = value.length === 1 && this._isExclusive(value[0]);
        const newValueIsExclusive = this._isExclusive(option);

        if (newValueIsExclusive || oldValueIsExclusive) {
          newValue = [option];
        } else {
          newValue.push(option);
        }

        if (newValueIsExclusive && this.state.otherChecked) {
          this.setState({ otherChecked: false, otherInputValue: '' });
        }
      }

      onChange(id, newValue);
    } else {
      this.setState({ otherChecked: false, otherInputValue: '' });
      onChange(id, option);
    }
  }

  _handleLoadMore() {
    this.setState({ loadMax: true });
  }

  _handleOnClickOther() {
    const { id, multiValue, onChange } = this.props;
    const { otherChecked, otherInputValue } = this.state;
    const value = Options._getValue(this.props.value);

    if (multiValue && otherChecked) {
      const index = value.indexOf(otherInputValue);

      if (index !== -1) {
        const newValue = value.slice(0, value.length);
        newValue.splice(index, 1);
        onChange(id, newValue);
      }

      this.setState({ otherChecked: false, otherInputValue: '' });
    } else if (!otherChecked) {
      this.setState({ otherChecked: true });
      const oldValueIsExclusive = value.length === 1 && this._isExclusive(value[0]);

      if (!multiValue || oldValueIsExclusive) {
        onChange(id, '');
      }
    }
  }

  _handleOnChangeOtherInput(inputId, inputValue) {
    const { id, multiValue, onChange } = this.props;
    const { otherInputValue } = this.state;
    const value = Options._getValue(this.props.value);

    if (multiValue) {
      const newValue = value.slice(0, value.length);
      const index = value.indexOf(otherInputValue);

      if (index !== -1) {
        newValue.splice(index, 1, inputValue);
      } else {
        newValue.push(inputValue);
      }

      onChange(id, newValue);
    } else {
      onChange(id, inputValue);
    }

    this.setState({ otherInputValue: inputValue });
  }

  render() {
    const {
      id,
      loadMore,
      horizontal,
      className,
      syncOptions,
      value,
      otherEnabled,
      otherInputProps,
      type,
      optionProps
    } = this.props;
    const { loadMax, otherChecked, otherInputValue } = this.state;
    const options = syncOptions ? Options._syncValuesAndOptions(value, this.props.options) : this.props.options;
    const optionComponents = this._getOptionComponents(options);

    return (
      <div id={id} className={`pxl-options ${className}`}>
        <div className={`${horizontal ? 'pxl-options--horizontal' : ''}`}>
          {optionComponents}
          <OtherOptionControl
            type={type}
            enabled={otherEnabled}
            checked={otherChecked}
            onChange={this._handleOnClickOther}
            optionProps={optionProps}
          />
          <OtherOptionInput
            checked={otherChecked}
            onChange={this._handleOnChangeOtherInput}
            value={otherInputValue}
            inputProps={otherInputProps}
          />
        </div>
        <LoadMore
          onClick={this._handleLoadMore}
          visible={
            !loadMax &&
            loadMore &&
            options &&
            options.length > 0 &&
            optionComponents &&
            optionComponents.length < options.length
          }
        />
      </div>
    );
  }
}

Options.propTypes = {
  className: PropTypes.string,
  exclusiveOptions: PropTypes.array,
  horizontal: PropTypes.bool,
  id: PropTypes.string,
  initShown: PropTypes.number,
  loadMore: PropTypes.bool,
  maxShown: PropTypes.number,
  multiValue: PropTypes.bool,
  onChange: PropTypes.func,
  optionProps: PropTypes.shape({
    className: PropTypes.string
  }),
  options: PropTypes.array,
  otherEnabled: PropTypes.bool,
  otherInputProps: PropTypes.object,
  syncOptions: PropTypes.bool,
  type: PropTypes.oneOf(['pills', 'checklist', 'radio']),
  value: PropTypes.oneOfType([PropTypes.array, PropTypes.string])
};

Options.defaultProps = {
  className: '',
  exclusiveOptions: [],
  horizontal: false,
  initShown: 5,
  loadMore: false,
  maxShown: -1,
  multiValue: true,
  optionProps: {
    className: ''
  },
  options: [],
  otherEnabled: false,
  otherInputProps: {},
  syncOptions: false,
  type: 'checklist',
  value: []
};

export default Options